/* ==========================================================================
   FLIPSI · SHOP ALL · V3 behaviour layer
   Loaded (deferred) by every flipsi-shopall-v3-* section. The browser
   de-duplicates by src, so loading it from each section is free and keeps
   every section self-sufficient in the theme editor.

   The page is deliberately built as several sections, so this layer works
   document-wide rather than per-section: the filter chips in the rail
   section must hide cards living in the shelf sections, and the count must
   total across all of them.

   Everything is idempotent. init() may run many times (first paint, and
   again on every shopify:section:load while Melissa edits) and will only
   ever wire an element once.

   LANDING PRE-FILTERED. The page accepts a ?shelf= parameter so buttons
   elsewhere on the site can land a visitor with the right filter already
   on: ?shelf=singles, sets, plus, ships, reserve, accessories, complete.
   The parameter is read once, on first paint only, so clicking the chips
   afterwards always wins.
   ========================================================================== */
(function () {
  'use strict';

  var BOUND = 'data-sa-bound';
  var URL_SHELF_APPLIED = false;

  function all(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function roots() { return all('[data-sa-root]'); }
  function cards() { return all('[data-sa-card]'); }

  /* ---------- the filter chips ------------------------------------------ */
  function applyFilter(key) {
    var list = cards();
    var total = list.length;
    var shown = 0;

    list.forEach(function (card) {
      var cat = card.getAttribute('data-sa-cat') || '';
      var state = card.getAttribute('data-sa-state') || '';
      var show = key === 'all' || cat === key || (key === 'reserve' && state === 'reserve') || (key === 'ships' && state === 'ship');
      card.classList.toggle('is-hidden', !show);
      if (show) { shown++; }
    });

    /* a shelf with nothing left steps aside, and so does its heading */
    all('[data-sa-shelf]').forEach(function (shelf) {
      var any = all('[data-sa-card]', shelf).some(function (c) {
        return !c.classList.contains('is-hidden');
      });
      shelf.hidden = !any;
      var empty = shelf.querySelector('[data-sa-empty]');
      if (empty) { empty.hidden = any; }
    });

    /* the mystery beat is a whole-range moment, so it rests while filtering */
    all('[data-sa-mystery]').forEach(function (m) {
      m.hidden = key !== 'all';
    });

    all('[data-sa-chip]').forEach(function (chip) {
      var on = chip.getAttribute('data-sa-f') === key;
      chip.classList.toggle('is-on', on);
      chip.setAttribute('aria-pressed', String(on));
    });

    all('[data-sa-count]').forEach(function (el) {
      var tpl = el.getAttribute('data-sa-count-tpl') || '{shown} of {total} items';
      el.textContent = tpl.replace('{shown}', shown).replace('{total}', total);
    });
  }

  /* ---------- ?shelf= landing filter ------------------------------------ */
  function urlShelf() {
    var map = {
      all: 'all',
      single: 'single', singles: 'single',
      set: 'set', sets: 'set',
      plus: 'plus',
      reserve: 'reserve', reserving: 'reserve', 'reserving-now': 'reserve',
      ship: 'ships', ships: 'ships', 'ships-now': 'ships',
      accessory: 'accessory', accessories: 'accessory', board: 'accessory',
      complete: 'complete'
    };
    try {
      var q = new URLSearchParams(window.location.search).get('shelf');
      if (!q) { return null; }
      return map[q.toLowerCase().trim()] || null;
    } catch (e) { return null; }
  }

  function initChips() {
    all('[data-sa-chip]').forEach(function (chip) {
      if (chip.getAttribute(BOUND) === '1') { return; }
      chip.setAttribute(BOUND, '1');
      chip.addEventListener('click', function () {
        applyFilter(chip.getAttribute('data-sa-f') || 'all');
      });
    });
  }

  /* ---------- show the artwork ------------------------------------------
     Flips every single puzzle from its box to its FRONT artwork. Both faces
     are rendered by Liquid, so nothing is guessed here and no secret-side
     image can ever be reached by this control. */
  function initArtButtons() {
    all('[data-sa-artbtn]').forEach(function (btn) {
      if (btn.getAttribute(BOUND) === '1') { return; }
      btn.setAttribute(BOUND, '1');
      btn.addEventListener('click', function () {
        var on = !document.documentElement.hasAttribute('data-sa-showart');
        if (on) {
          document.documentElement.setAttribute('data-sa-showart', '1');
        } else {
          document.documentElement.removeAttribute('data-sa-showart');
        }
        roots().forEach(function (r) { r.classList.toggle('is-showart', on); });
        all('[data-sa-artbtn]').forEach(function (b) {
          b.classList.toggle('is-on', on);
          b.setAttribute('aria-pressed', String(on));
          var off = b.getAttribute('data-sa-label-off') || 'Show the artwork';
          var onLabel = b.getAttribute('data-sa-label-on') || 'Show the boxes';
          b.textContent = on ? onLabel : off;
        });
      });
    });
    /* a section reloaded in the editor must adopt the current state */
    if (document.documentElement.hasAttribute('data-sa-showart')) {
      roots().forEach(function (r) { r.classList.add('is-showart'); });
    }
  }

  /* ---------- the ? curiosity seal. A ? always opens something ----------- */
  function initSeals() {
    all('[data-sa-fc]').forEach(function (host) {
      if (host.getAttribute(BOUND) === '1') { return; }
      host.setAttribute(BOUND, '1');

      var btn = host.querySelector('.sa-seal-btn');
      var panel = host.querySelector('.sa-fc-panel');
      if (!btn || !panel) { return; }

      var setFocusable = function (open) {
        all('a,button', panel).forEach(function (el) {
          if (open) { el.removeAttribute('tabindex'); } else { el.setAttribute('tabindex', '-1'); }
        });
      };
      var setOpen = function (open) {
        host.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', String(open));
        panel.setAttribute('aria-hidden', String(!open));
        setFocusable(open);
      };

      setOpen(false);
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        setOpen(!host.classList.contains('is-open'));
      });
      var close = host.querySelector('.sa-fc-close');
      if (close) {
        close.addEventListener('click', function (e) {
          e.stopPropagation();
          setOpen(false);
          btn.focus();
        });
      }
    });
  }

  /* ---------- the mystery count -----------------------------------------
     Counts the puzzle artworks actually on show in the singles shelves. A
     twin pack carries two, which is why the number is summed from the cards
     rather than counted. Liquid already printed a correct number, so this
     only keeps it honest if Melissa hides or adds a card. */
  function initMysteryCount() {
    var targets = all('[data-sa-artcount]');
    if (!targets.length) { return; }
    var n = 0;
    cards().forEach(function (c) {
      if ((c.getAttribute('data-sa-cat') || '') !== 'single') { return; }
      n += parseInt(c.getAttribute('data-sa-artworks') || '1', 10) || 1;
    });
    if (!n) { return; }
    targets.forEach(function (el) { el.textContent = String(n); });
  }

  /* ---------- gentle scroll fades, imagery only -------------------------- */
  var io = null;
  function initFades() {
    var els = all('.sa-fade').filter(function (el) { return el.getAttribute(BOUND) !== '1'; });
    els.forEach(function (el) { el.setAttribute(BOUND, '1'); });
    if (!els.length) { return; }

    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    if (!io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      }, { threshold: 0.15 });
    }
    els.forEach(function (el) { io.observe(el); });
    /* safety net: nothing on this page is ever allowed to stay invisible */
    window.setTimeout(function () {
      els.forEach(function (el) { el.classList.add('is-in'); });
    }, 2500);
  }

  /* ---------- buyable first ----------------------------------------------
     Within every shelf grid, ships-now cards come first, then reserve, then
     sold out (requested by Demi, 16 Aug 2026: an AU visitor was scrolling
     sold-out cards to reach buyable ones). The curated order is kept inside
     each group: the sort is stable by original index. States were resolved
     by Liquid in the visitor's own market, so AU and US visitors each get
     their own true order. Idempotent: when the order is already correct,
     nothing is re-appended. */
  var STATE_RANK = { ship: 0, reserve: 1, soldout: 2 };
  function initBuyableFirst() {
    var grids = [];
    cards().forEach(function (c) {
      var g = c.parentElement;
      if (g && grids.indexOf(g) === -1) { grids.push(g); }
    });
    grids.forEach(function (grid) {
      var list = all('[data-sa-card]', grid).filter(function (c) {
        return c.parentElement === grid;
      });
      var sorted = list.map(function (c, i) { return { c: c, i: i }; })
        .sort(function (a, b) {
          var ra = STATE_RANK[a.c.getAttribute('data-sa-state')];
          var rb = STATE_RANK[b.c.getAttribute('data-sa-state')];
          if (ra === undefined) { ra = 9; }
          if (rb === undefined) { rb = 9; }
          return ra === rb ? a.i - b.i : ra - rb;
        })
        .map(function (x) { return x.c; });
      var changed = sorted.some(function (c, i) { return c !== list[i]; });
      if (changed) {
        sorted.forEach(function (c) { grid.appendChild(c); });
      }
    });
  }

  function init() {
    roots().forEach(function (r) { r.classList.add('sa-js'); });
    initBuyableFirst();
    initChips();
    initArtButtons();
    initSeals();
    initMysteryCount();
    initFades();

    /* first paint honours ?shelf=, once; afterwards, and on every editor
       reload, whichever chip is currently on is simply re-applied */
    var key = null;
    if (!URL_SHELF_APPLIED) {
      URL_SHELF_APPLIED = true;
      key = urlShelf();
    }
    if (!key) {
      var current = document.querySelector('[data-sa-chip].is-on');
      key = current ? (current.getAttribute('data-sa-f') || 'all') : 'all';
    }
    applyFilter(key);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:reorder', init);
})();
